import { autoinject, transient } from "aurelia-dependency-injection";
import { ValidationController, ValidationRules } from "aurelia-validation";
import * as moment from "moment";
import { CustomValidationRenderer } from "../../../../../../../client-validation-system/custom-validation-renderer";
import { EntityTypeConfigurationModel } from "../../../../../../../models/basics/basics.models";
import { NotificationClientService, NotificationType } from "../../../../../../../services/basics/notification-client.service";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { PTDateTimeHelper } from "../../../helpers/pt-datetime-helper";
import { NewPortCallTypeModel } from "../../../models/create-vessel-port-arrival-model";
import { PortsTerminalBerthsResponseItem, PortTerminalAndBerthResponse, PTEntityTypeConfigurationConstants } from "../../../models/ports-and-terminals.models";
import { PortsAndTerminalsService } from "../../../services/ports-and-terminals.service";
import { AddVesselToScheduleDialogModel, IPartialAddVesselSchedule } from "../add-vessel-to-schedule-dialog";
import { useView } from "aurelia-framework";

@autoinject
@transient()
@useView("views/pages/modules/ports-and-terminals/dialogs/add-vessel-to-schedule-dialog/partial-add-manual-vessel-schedule/partial-add-manual-vessel-schedule.html")
export class PartialAddManualVesselSchedule implements IPartialAddVesselSchedule {

    item: NewPortCallTypeModel;
    departureTime: string;
    arrivalTime: string;
    berths: PortsTerminalBerthsResponseItem[] = [];
    terminals: PortsTerminalBerthsResponseItem[] = [];
    allTerminals: PortsTerminalBerthsResponseItem[] = [];
    allBerths: PortsTerminalBerthsResponseItem[] = [];
    ports: PortsTerminalBerthsResponseItem[] = [];
    private _portOffset: string;

    onSave: () => void;
    showETD: boolean = false;

    public vesselEntityTypes: EntityTypeConfigurationModel[] = this.getVesselSearchConfig();

    constructor(private validationController: ValidationController,
        private portsAndTerminalsService: PortsAndTerminalsService,
        private notificationClientService: NotificationClientService) {
        this.validationController.reset();
        this.validationController.addRenderer(new CustomValidationRenderer());
    }

    dispose(): void {
        this.item = null;
    }

    canSubmit: boolean = true;

    activate(model: AddVesselToScheduleDialogModel) {
        this.onSave = model.onSave;
        this.showETD = model.showETD ? model.showETD : false;
        this.initializeItem();
        this.loadPortsTerminalsAndBerths(model.portTerminalAndBerthResponse);
    }

    initializeItem() {
        this.item = {
            portCallId: null,
            portSKey: null,
            vesselSKey: null,
            arrivalTime: "",
            departureTime: "",
            departureDate: null,
            arrivalDate: null,
            berthSKey: null,
            terminalSKey: null
        };
    }

    submit() {
        return new Promise<void>((resolve, reject) => {
            this.initValidationRules();
            this.validationController.validate().then(async response => {
                if (!response.valid) {
                    reject();
                    return;
                }

                this._portOffset = await this.portsAndTerminalsService.getPortOffset(this.item.portSKey);
                
                //cloning before save so that time/date in ui would not show different than value set at any point, since we are change to utc using port timezone instead of user timezone
                const model = CloneHelper.cloneObject(this.item);
                model.arrivalDate = PTDateTimeHelper.convertToUtc(this.item.arrivalDate, this.item.arrivalTime, this._portOffset);
                model.departureDate = PTDateTimeHelper.convertToUtc(this.item.departureDate, this.item.departureTime, this._portOffset);
                this.portsAndTerminalsService.createVesselPortArrival({ vesselPortCalls: [model] })
                    .then(() => {
                        this.notificationClientService.show(``, `Vessel Port arrival created`, NotificationType.Success);
                        if (this.onSave) {
                            this.onSave();
                        }
                        resolve();
                    }).catch(e => reject(e));
            }).catch(e => reject(e));
        });

    }

    loadPortsTerminalsAndBerths(portTerminalandBerthResponse: PortTerminalAndBerthResponse) {
        if (portTerminalandBerthResponse) {
            this.setPortsTerminalsAndBerths(portTerminalandBerthResponse);
        }
        else {
            //fallback
            this.portsAndTerminalsService.loadPortsTerminalsAndBerths()
                .then(res => {
                    this.setPortsTerminalsAndBerths(res);
                });
        }
    }

    setPortsTerminalsAndBerths(res: PortTerminalAndBerthResponse) {
        this.allBerths = res.berths;
        this.ports = res.ports;
        this.allTerminals = res.terminals;
    }

    getTerminals($event) {
        if ($event?.detail?.item) {
            this.terminals = this.allTerminals.filter(x => x.parentId == $event.detail.item.id);
            if (this.terminals.findIndex(x => x.id == this.item.terminalSKey) < 0) {
                this.item.terminalSKey = null
                this.item.berthSKey = null
            }
        }
    }

    getBerths($event) {
        if ($event?.detail?.item) {
            this.berths = this.allBerths.filter(x => x.parentId == $event.detail.item.id);

            if (this.berths.findIndex(x => x.id == this.item.berthSKey) < 0) {
                this.item.berthSKey = null
            }
        }
    }

    onVesselSelected(vessel: any) {
        this.item.vesselSKey = vessel ? parseInt(vessel.vesselSKey) : null;
    }

    initValidationRules() {
        ValidationRules
            .ensure<NewPortCallTypeModel, number>(vpa => vpa.portSKey)
            .displayName('Port')
            .required()

            .ensure<number>(vpa => vpa.vesselSKey)
            .displayName('Vessel')
            .required()

            .ensure<Date | string>(vpa => vpa.arrivalDate)
            .displayName('Arrival date')
            .required()
            .satisfies(x => moment(x).isSameOrAfter(moment(), 'day'))
            .withMessage("Arrival date must be greater than today's date")

            .ensure<string | Date>(vpa => vpa.arrivalTime)
            .displayName('Arrival time')
            .required()
            .satisfies(x => {
                const date = moment(x);
                const today = moment();

                return date.hour() > today.hour()
                    || (date.hour() == today.hour() && date.minutes() >= today.minutes());
            })
            .when(x => moment(x.arrivalDate).isSame(moment(), 'day'))
            .withMessage("Arrival time must be greater than current time")
            .on(this.item);
    }

    getVesselSearchConfig(): EntityTypeConfigurationModel[] {
        return PTEntityTypeConfigurationConstants.filter(x => x.key == "PTVesselSearch");
    }
}